import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { logger } from './logger';
import { queryClient } from './queryClient';
import { getApiBaseUrl } from '../config/env';
import { apiErrorMessage, toApiError } from './errors';

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retryCount?: number;
  }
}

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 500;
const RETRYABLE_METHOD = 'GET';
// Canonical SSO storage key. Do not introduce aliases: storageState, logout,
// the request interceptor and tests must all use the same boundary contract.
const TOKEN_KEY = 'jwt';
const USER_KEY = 'user';
const VIEW_ALL_KEY = 'wiki-view-all-depts';
const ACTIVE_DEPT_KEY = 'wiki-active-dept';
const requestStartTimes = new WeakMap<object, number>();
let authRedirectInProgress = false;

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string | null): void {
  if (token === null) localStorage.removeItem(TOKEN_KEY);
  else localStorage.setItem(TOKEN_KEY, token);
}

export function getStoredUser<T = unknown>(): T | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function setStoredUser<T = unknown>(user: T | null): void {
  if (user === null) localStorage.removeItem(USER_KEY);
  else localStorage.setItem(USER_KEY, JSON.stringify(user));
}

function shouldRetry(error: { config?: { method?: string }; response?: { status?: number } }): boolean {
  if (error.config?.method?.toUpperCase() !== RETRYABLE_METHOD) return false;
  if ((error.response?.status ?? 0) >= 500) return true;
  if (!error.response) return true;
  return false;
}

async function retryWithBackoff(config: InternalAxiosRequestConfig, attempt: number): Promise<AxiosResponse> {
  const delay = RETRY_DELAY_MS * 2 ** (attempt - 1);
  logger.warn(`Reintentando ${config.method?.toUpperCase()} ${config.url} (intento ${attempt}/${MAX_RETRIES}) en ${delay}ms`);
  await new Promise((resolve) => setTimeout(resolve, delay));
  return api.request({ ...config, _retryCount: attempt } as InternalAxiosRequestConfig);
}

const api: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Accept': 'application/json',
  },
  timeout: 30000,
});

// Interceptor request: adjuntar `Authorization: Bearer <jwt>` si hay token.
api.interceptors.request.use(
  (config) => {
    requestStartTimes.set(config, performance.now());
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['X-Wiki-View-All'] = localStorage.getItem(VIEW_ALL_KEY) === 'true' ? 'true' : 'false';
    const activeDepartment = localStorage.getItem(ACTIVE_DEPT_KEY);
    if (activeDepartment) {
      config.headers['X-Wiki-Active-Department'] = activeDepartment;
    }
    logger.request(config.method?.toUpperCase() || '?', config.url || '', config.data);
    return config;
  },
  (error) => {
    logger.error('Request interceptor', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response: AxiosResponse) => {
    const start = requestStartTimes.get(response.config);
    const elapsed = start === undefined ? 0 : performance.now() - start;
    const serverElapsed = Number(response.headers['x-process-time']) * 1000;
    logger.response(
      response.config?.method?.toUpperCase() || '?',
      response.config?.url || '',
      response.status,
      Math.round(elapsed || (Number.isFinite(serverElapsed) ? serverElapsed : 0))
    );
    return response;
  },
  (error) => {
    const normalized = toApiError(error);
    const status = normalized?.status || error.response?.status;
    const code = normalized?.code;
    const message = normalized?.message || error.message || 'Error inesperado';
    const url = error.config?.url || '';
    const method = error.config?.method?.toUpperCase() || '?';

    logger.response(method, url, status || 0, 0);

    const retryCount = error.config?._retryCount || 0;
    if (shouldRetry(error) && retryCount < MAX_RETRIES) {
      return retryWithBackoff(error.config, retryCount + 1);
    }

    if (status === 401 || code === 'AUTH_TOKEN_MISSING' || code === 'AUTH_TOKEN_INVALID' || code === 'AUTH_TOKEN_EXPIRED') {
      const isLoginAttempt = url.includes('/auth/login');
      if (isLoginAttempt) {
        return Promise.reject(error);
      }
      if (authRedirectInProgress) {
        return Promise.reject(error);
      }
      authRedirectInProgress = true;
      logger.warn('401 — Token invalido o expirado, limpiando y redirigiendo a /login');
      setStoredToken(null);
      setStoredUser(null);
      queryClient.clear();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    if (status === 403 || code === 'AUTH_APP_ACCESS_DENIED' || code === 'FORBIDDEN') {
      logger.warn('403 Acceso denegado:', message);
      return Promise.reject(error);
    }

    if (status === 422 || code === 'VALIDATION_ERROR') {
      logger.info('422 Validacion:', message);
      return Promise.reject(error);
    }

    if ((status && status >= 500) || code === 'DATABASE_UNAVAILABLE' || code === 'SSO_UNAVAILABLE' || code === 'INTERNAL_ERROR') {
      logger.error('5xx Servidor', { status, message, url });
      return Promise.reject(error);
    }

    if (!error.response) {
      logger.error('Conexion', { message, url });
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

/**
 * Traduce codigos de error del boundary a mensajes seguros para la UI.
 */
export function loginErrorMessage(err: unknown): string {
  const normalized = toApiError(err);
  const status = normalized?.status;
  const code = normalized?.code;
  if (code === 'AUTH_INVALID_CREDENTIALS') return 'Usuario o contrasena incorrectos.';
  if (code === 'AUTH_APP_ACCESS_DENIED') return 'No tiene acceso a esta aplicacion.';
  if (code === 'AUTH_RATE_LIMITED') return 'Demasiados intentos. Espere un momento.';
  if (code === 'SSO_UNAVAILABLE') return 'El servicio de autenticacion no esta disponible.';
  if (code === 'SSO_SERVICE_TOKEN_NOT_CONFIGURED') return 'La autenticacion no esta configurada.';
  if (code === 'SSO_SERVICE_TOKEN_INVALID' || code === 'SSO_SERVICE_TOKEN_SCOPE_MISSING') {
    return 'El servicio de autenticacion no esta disponible.';
  }
  if (status === 401) return 'Usuario o contrasena incorrectos.';
  if (status === 403) return 'No tiene acceso a esta aplicacion.';
  if (status === 429) return 'Demasiados intentos. Espere un momento.';
  if (status && status >= 500) return 'Error del servidor. Intente mas tarde.';
  return normalized?.message || apiErrorMessage(err, 'Error de autenticacion.');
}

export const uploadFile = async (
  url: string,
  file: File,
  additionalData: Record<string, unknown> = {}
): Promise<AxiosResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  Object.entries(additionalData).forEach(([key, value]) => {
    formData.append(key, String(value));
  });
  logger.info('Upload:', url, file.name);
  return api.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const downloadFile = async (url: string, filename?: string): Promise<void> => {
  logger.info('Download:', url);
  const response = await api.get(url, { responseType: 'blob' });
  const blob = new Blob([response.data]);
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename || response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'download';
  link.click();
  URL.revokeObjectURL(link.href);
  logger.info('Download complete:', link.download);
};

export default api;
